/**
 * Direction Label Utilities for Event Detection
 *
 * Maps axis indices (1, 2, 3) to vibration direction labels based on account preference.
 * - TAR (preference=1): Tangential, Axial, Radial
 * - HAV (preference=2): Horizontal, Axial, Vertical
 */

import i18n from 'i18next';
import { humanize } from 'common/helpers/humanize';

/**
 * Normalize API/UI direction strings to translation keys (e.g. "Tangential" -> "tangential").
 * @param {string} direction
 * @returns {string}
 */
export const normalizeDirectionKey = direction => String(direction || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

/**
 * Localized direction label for tables, charts, and filters.
 * @param {function} t - i18next `t`
 * @param {string} direction - e.g. "Tangential", "tangential", "axial"
 * @returns {string}
 */
export const getDirectionDisplayName = (t, direction) => {
  if (!direction) return '';
  const key = normalizeDirectionKey(direction);
  return t(`common.directions.${key}`, { defaultValue: humanize(direction) });
};

/**
 * Get direction label for a single axis index
 * @param {number} axisIndex - Axis index (1, 2, or 3)
 * @param {number} vibrationDirectionPreference - Account preference (1 for TAR, 2 for HAV)
 * @returns {string} Direction label
 */
export const getAxisDirectionLabel = (tOrPreference, axisIndex, vibrationDirectionPreference = 1) => {
  const { t, preference } = normalizeArgs(tOrPreference, vibrationDirectionPreference);
  const labels = getAxisDirectionLabels(t, preference);
  return labels[axisIndex] || '';
};

const normalizeArgs = (tOrPreference, vibrationDirectionPreference) => {
  // Back-compat:
  // - getAxisDirectionLabels(preference)
  // - getAxisDirectionLabels(t, preference)
  const maybeT = typeof tOrPreference === 'function' ? tOrPreference : null;
  const preference = typeof tOrPreference === 'number' ? tOrPreference : vibrationDirectionPreference;
  return { t: maybeT || i18n.t.bind(i18n), preference };
};

/**
 * Get mapping object of axis indices to direction labels
 * @param {number} vibrationDirectionPreference - Account preference (1 for TAR, 2 for HAV)
 * @returns {Object} Mapping object {1: 'Tangential', 2: 'Axial', 3: 'Radial'} or HAV equivalent
 */
export const getAxisDirectionLabels = (tOrPreference, vibrationDirectionPreference = 1) => {
  const { t, preference } = normalizeArgs(tOrPreference, vibrationDirectionPreference);
  if (preference === 2) {
    // HAV: Horizontal, Axial, Vertical
    return {
      1: t('common.directions.horizontal'),
      2: t('common.directions.axial'),
      3: t('common.directions.vertical')
    };
  }
  // TAR: Tangential, Axial, Radial (default)
  return {
    1: t('common.directions.tangential'),
    2: t('common.directions.axial'),
    3: t('common.directions.radial')
  };
};

/**
 * Get lowercase axis key for threshold mapping (used internally for data structure)
 * @param {number} axisIndex - Axis index (1, 2, or 3)
 * @returns {string} Lowercase axis key ('x', 'y', 'z')
 */
export const getAxisKey = (axisIndex) => {
  const axisKeyMap = { 1: 'x', 2: 'y', 3: 'z' };
  return axisKeyMap[axisIndex] || '';
};

/**
 * Get mapping of axis indices to backend direction names (lowercase)
 * Backend stores tag directions matching account preference
 * @param {number} vibrationDirectionPreference - Account preference (1 for TAR, 2 for HAV)
 * @returns {Object} Mapping object {1: 'tangential', 2: 'axial', 3: 'radial'} or HAV equivalent
 */
export const getAxisToBackendDirection = (vibrationDirectionPreference = 1) => {
  if (vibrationDirectionPreference === 2) {
    // HAV: Horizontal, Axial, Vertical
    return {
      1: 'horizontal',
      2: 'axial',
      3: 'vertical'
    };
  }
  // TAR: Tangential, Axial, Radial (default)
  return {
    1: 'tangential',
    2: 'axial',
    3: 'radial'
  };
};
